import React, {
  useState, useEffect, useCallback, useImperativeHandle, forwardRef,
} from 'react';
import {
  Box,
  VStack,
  HStack,
  Textarea,
  TextareaInput,
  Text,
  Input,
  InputField,
  Select,
  SelectTrigger,
  SelectInput,
  SelectIcon,
  SelectPortal,
  SelectBackdrop,
  SelectContent,
  SelectItem,
  ScrollView,
  Pressable,
} from '@gluestack-ui/themed';
import { ChevronDown } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { MathRenderer, containsMath } from '@/common/components/MathRenderer';
import { OptionEditor, type MCQOption } from './OptionEditor';
import { ImageUploader } from './ImageUploader';

export interface MCQQuestion {
  id: string;
  text: string;
  explanation?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
  options: MCQOption[];
  imageUrl?: string | null;
  imageAlt?: string | null;
}

export interface QuestionEditorHandle {
  getQuestion: () => MCQQuestion | null;
  hasChanges: () => boolean;
}

interface QuestionEditorProps {
  moduleId: string;
  version: string;
  taskId: string;
  question: MCQQuestion;
  isReadOnly?: boolean;
}

interface ValidationErrors {
  text?: string;
  options?: string;
  correctAnswer?: string;
  optionTexts?: Record<string, string>;
}

export const QuestionEditor = forwardRef<QuestionEditorHandle, QuestionEditorProps>(({
  moduleId,
  version,
  taskId,
  question: initialQuestion,
  isReadOnly = false,
}, ref) => {
  const { t } = useTranslation();
  const [question, setQuestion] = useState<MCQQuestion>(initialQuestion);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Sync server-managed image fields from cache for the question AND
  // each option, without resetting user's in-progress text/option edits.
  useEffect(() => {
    setQuestion((prev) => ({
      ...prev,
      imageUrl: initialQuestion.imageUrl,
      imageAlt: initialQuestion.imageAlt,
      options: prev.options.map((opt) => {
        const cached = initialQuestion.options.find((o) => o.id === opt.id);
        if (!cached) return opt;
        return { ...opt, imageUrl: cached.imageUrl, imageAlt: cached.imageAlt };
      }),
    }));
  }, [initialQuestion]);

  useEffect(() => {
    setHasChanges(JSON.stringify(question) !== JSON.stringify(initialQuestion));
  }, [question, initialQuestion]);

  const validate = useCallback((): boolean => {
    const newErrors: ValidationErrors = {};
    if (!question.text?.trim()) {
      newErrors.text = t('lecturer.moduleEditor.taskEditor.mcq.errors.questionRequired');
    }
    if (question.options.length < 2) {
      newErrors.options = t('lecturer.moduleEditor.taskEditor.mcq.errors.minOptions');
    }
    const correctCount = question.options.filter((o) => o.isCorrect).length;
    if (correctCount !== 1) {
      newErrors.correctAnswer = t('lecturer.moduleEditor.taskEditor.mcq.errors.oneCorrect');
    }
    const optionTexts: Record<string, string> = {};
    question.options.forEach((o) => {
      if (!o.text?.trim()) optionTexts[o.id] = t('lecturer.moduleEditor.taskEditor.mcq.errors.optionRequired');
    });
    if (Object.keys(optionTexts).length) newErrors.optionTexts = optionTexts;

    setErrors(newErrors);
    return !Object.keys(newErrors).length;
  }, [question, t]);

  useImperativeHandle(ref, () => ({
    getQuestion: () => (validate() ? question : null),
    hasChanges: () => hasChanges,
  }));

  const handleAddOption = useCallback(() => {
    setQuestion((prev) => ({
      ...prev,
      options: [...prev.options, {
        id: `opt-${Date.now()}`, text: '', isCorrect: false, feedback: null,
      }],
    }));
  }, []);

  const handleUpdateOption = useCallback((index: number, updated: MCQOption) => {
    setQuestion((prev) => ({
      ...prev,
      options: prev.options.map((o, i) => (i === index ? updated : o)),
    }));
  }, []);

  const handleDeleteOption = useCallback((index: number) => {
    setQuestion((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  }, []);

  const handleToggleCorrect = useCallback((index: number) => {
    setQuestion((prev) => ({
      ...prev,
      options: prev.options.map((o, i) => ({ ...o, isCorrect: i === index })),
    }));
  }, []);

  return (
    <ScrollView bg="$white">
      <VStack space="md" px="$4" py="$4">
        {/* Question Text */}
        <VStack space="xs">
          <Text size="xs" fontWeight="medium" color="$textSubtle" textTransform="uppercase">
            Question
          </Text>
          <Textarea isDisabled={isReadOnly} isInvalid={!!errors.text} size="sm">
            <TextareaInput
              testID="question-editor-text-input"
              value={question.text}
              onChangeText={(text) => setQuestion({ ...question, text })}
              placeholder="Enter your question here"
            />
          </Textarea>
          {!!errors.text && (
            <Text testID="question-editor-text-error" size="xs" color="$statusError">{errors.text}</Text>
          )}
        </VStack>

        {/* Math Preview */}
        {containsMath(question.text) && (
          <Box testID="question-editor-math-preview" p="$2" bg="$bgSubtle" borderRadius="$sm">
            <Text size="xs" fontWeight="medium" color="$textSubtle" mb="$1">Preview</Text>
            <MathRenderer>{question.text}</MathRenderer>
          </Box>
        )}

        {/* Image Upload - state flows from cache via initialQuestion sync */}
        {!isReadOnly && (
          <ImageUploader
            moduleId={moduleId}
            version={version}
            taskId={taskId}
            questionId={question.id}
            type="question"
            currentImageUrl={question.imageUrl || undefined}
          />
        )}

        {/* Difficulty & Explanation */}
        <HStack space="md">
          <VStack space="xs" width={140}>
            <Text size="xs" fontWeight="medium" color="$textSubtle" textTransform="uppercase">
              Difficulty
            </Text>
            <Select
              testID="question-editor-difficulty-selector"
              selectedValue={question.difficulty || 'medium'}
              onValueChange={(v) => setQuestion({ ...question, difficulty: v as 'easy' | 'medium' | 'hard' })}
              isDisabled={isReadOnly}
            >
              <SelectTrigger size="sm">
                <SelectInput placeholder="Difficulty" />
                <SelectIcon as={ChevronDown} />
              </SelectTrigger>
              <SelectPortal>
                <SelectBackdrop />
                <SelectContent>
                  <SelectItem label="Easy" value="easy" />
                  <SelectItem label="Medium" value="medium" />
                  <SelectItem label="Hard" value="hard" />
                </SelectContent>
              </SelectPortal>
            </Select>
          </VStack>
          <VStack space="xs" flex={1}>
            <Text size="xs" fontWeight="medium" color="$textSubtle" textTransform="uppercase">
              Explanation (optional)
            </Text>
            <Input isDisabled={isReadOnly} size="sm">
              <InputField
                testID="question-editor-explanation-input"
                value={question.explanation || ''}
                onChangeText={(explanation) => setQuestion({ ...question, explanation })}
                placeholder="Why is the correct answer correct?"
              />
            </Input>
          </VStack>
        </HStack>

        {/* Divider */}
        <Box borderBottomWidth={1} borderColor="$borderLight" />

        {/* Options */}
        <VStack space="sm">
          <HStack justifyContent="space-between" alignItems="center">
            <Text size="xs" fontWeight="medium" color="$textSubtle" textTransform="uppercase">
              Options (
              {question.options.length}
              )
            </Text>
            {!isReadOnly && (
              <Pressable
                testID="question-editor-add-option-button"
                onPress={handleAddOption}
              >
                <Text size="xs" fontWeight="medium" color="$brandBase">
                  + Add Option
                </Text>
              </Pressable>
            )}
          </HStack>

          {!!errors.options && (
            <Text testID="question-editor-options-error" size="xs" color="$statusError">{errors.options}</Text>
          )}
          {!!errors.correctAnswer && (
            <Text testID="question-editor-correct-answer-error" size="xs" color="$statusError">{errors.correctAnswer}</Text>
          )}

          <VStack space="xs">
            {question.options.map((option, index) => (
              <OptionEditor
                key={option.id}
                moduleId={moduleId}
                version={version}
                taskId={taskId}
                questionId={question.id}
                option={option}
                index={index}
                onUpdate={(updated) => handleUpdateOption(index, updated)}
                onDelete={() => handleDeleteOption(index)}
                onToggleCorrect={() => handleToggleCorrect(index)}
                isReadOnly={isReadOnly}
                error={errors.optionTexts?.[option.id]}
              />
            ))}
          </VStack>
        </VStack>
      </VStack>
    </ScrollView>
  );
});

QuestionEditor.displayName = 'QuestionEditor';
